// ============================================================
// RIZKA TRAVEL CRM — MAIN LAYOUT WITH SIDEBAR
// File: src/components/Layout.js
// ============================================================

import React, { useState, useEffect } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useAuth, api } from '../context/AuthContext';
import toast from 'react-hot-toast';
import TopBarSearch from './TopBarSearch';

const navItems = [
    { path: '/dashboard', icon: '⊞', label: 'Dashboard' },
    { path: '/bookings', icon: '✈', label: 'Bookings' },
    { path: '/bookings/search', icon: '🔍', label: 'Search bookings' },
    { path: '/customers', icon: '👤', label: 'Customers' },
    { path: '/suppliers', icon: '🏨', label: 'Suppliers' },
    { path: '/documents', icon: '📄', label: 'Documents' },
    { path: '/tasks', icon: '✓', label: 'Tasks' },
    { path: '/reports', icon: '📊', label: 'Reports' },
];

const adminItems = [
    { path: '/users', icon: '👥', label: 'Users' },
    { path: '/settings', icon: '⚙', label: 'Settings' },
];

export default function Layout() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const [sidebarOpen, setSidebarOpen] = useState(true);
    const [taskCount, setTaskCount] = useState(0);

    // Open-tasks-assigned-to-me count for the sidebar badge.
    // Refreshes immediately whenever a task changes anywhere in the app (a
    // 'tasks:changed' window event, fired by the Tasks page and the add-task
    // modal), when the tab regains focus, and on a slow fallback interval (for
    // tasks another user assigns you while you're sitting on a page).
    useEffect(() => {
        let alive = true;
        const load = () => api.get('/tasks/inbox')
            .then(r => { if (alive) setTaskCount(r.data?.counts?.openAssignedToMe || 0); })
            .catch(() => {});
        load();
        const onChanged = () => load();
        const onFocus = () => { if (document.visibilityState === 'visible') load(); };
        window.addEventListener('tasks:changed', onChanged);
        window.addEventListener('focus', onFocus);
        document.addEventListener('visibilitychange', onFocus);
        const t = setInterval(load, 30000);
        return () => {
            alive = false;
            clearInterval(t);
            window.removeEventListener('tasks:changed', onChanged);
            window.removeEventListener('focus', onFocus);
            document.removeEventListener('visibilitychange', onFocus);
        };
    }, []);

    const handleLogout = async () => {
        await logout();
        toast.success('Logged out successfully.');
        navigate('/login');
    };

    return (
        <div style={{ display: 'flex', minHeight: '100vh', background: '#f0f4f8' }}>

            {/* Sidebar */}
            <div style={{
                width: sidebarOpen ? '240px' : '64px',
                background: '#102a43',
                display: 'flex',
                flexDirection: 'column',
                transition: 'width 0.2s',
                position: 'fixed',
                top: 0,
                left: 0,
                height: '100vh',
                zIndex: 100,
                overflowX: 'hidden'
            }}>
                {/* Logo area */}
                <div style={{
                    padding: '1rem',
                    borderBottom: '1px solid #243b53',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    minHeight: '64px'
                }}>
                    {sidebarOpen && (
                        <div>
                            <img
                                src="/logos/rizka-travel.png"
                                alt="Rizka Travel"
                                style={{ height: '28px' }}
                                onError={(e) => {
                                    e.target.style.display = 'none';
                                    e.target.nextSibling.style.display = 'block';
                                }}
                            />
                            <div style={{
                                display: 'none',
                                color: 'white',
                                fontWeight: '700',
                                fontSize: '0.875rem',
                                letterSpacing: '0.1em'
                            }}>
                                RIZKA TRAVEL
                            </div>
                        </div>
                    )}
                    <button
                        onClick={() => setSidebarOpen(!sidebarOpen)}
                        style={{
                            background: 'none',
                            border: 'none',
                            color: '#9fb3c8',
                            cursor: 'pointer',
                            fontSize: '1.25rem',
                            padding: '0.25rem',
                            marginLeft: sidebarOpen ? 0 : 'auto',
                            marginRight: sidebarOpen ? 0 : 'auto',
                            display: 'block'
                        }}
                    >
                        {sidebarOpen ? '◀' : '▶'}
                    </button>
                </div>

                {/* Navigation */}
                <nav style={{ flex: 1, padding: '0.5rem 0', overflowY: 'auto' }}>
                    {navItems.map(item => (
                        <NavLink
                            key={item.path}
                            to={item.path}
                            style={({ isActive }) => ({
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.75rem',
                                padding: '0.625rem 1rem',
                                color: isActive ? 'white' : '#829ab1',
                                textDecoration: 'none',
                                fontSize: '0.875rem',
                                fontWeight: isActive ? '500' : '400',
                                background: isActive ? '#243b53' : 'transparent',
                                borderLeft: isActive ? '3px solid #9fb3c8' : '3px solid transparent',
                                transition: 'all 0.15s',
                                whiteSpace: 'nowrap'
                            })}
                        >
                            <span style={{ fontSize: '1rem', minWidth: '20px', textAlign: 'center' }}>
                                {item.icon}
                            </span>
                            {sidebarOpen && item.label}
                            {item.path === '/tasks' && taskCount > 0 && sidebarOpen && (
                                <span style={{
                                    marginLeft: 'auto',
                                    background: '#e12d39',
                                    color: '#fff',
                                    borderRadius: '9999px',
                                    fontSize: '0.6875rem',
                                    fontWeight: 700,
                                    padding: '0.05rem 0.4rem',
                                    minWidth: '18px',
                                    textAlign: 'center',
                                    lineHeight: 1.6
                                }}>{taskCount}</span>
                            )}
                        </NavLink>
                    ))}

                    {/* Admin section */}
                    {user?.isAdmin && (
                        <>
                            {sidebarOpen && (
                                <div style={{
                                    padding: '0.5rem 1rem',
                                    color: '#486581',
                                    fontSize: '0.6875rem',
                                    fontWeight: '600',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.1em',
                                    marginTop: '0.5rem'
                                }}>
                                    Admin
                                </div>
                            )}
                            {adminItems.map(item => (
                                <NavLink
                                    key={item.path}
                                    to={item.path}
                                    style={({ isActive }) => ({
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '0.75rem',
                                        padding: '0.625rem 1rem',
                                        color: isActive ? 'white' : '#829ab1',
                                        textDecoration: 'none',
                                        fontSize: '0.875rem',
                                        fontWeight: isActive ? '500' : '400',
                                        background: isActive ? '#243b53' : 'transparent',
                                        borderLeft: isActive ? '3px solid #9fb3c8' : '3px solid transparent',
                                        transition: 'all 0.15s',
                                        whiteSpace: 'nowrap'
                                    })}
                                >
                                    <span style={{ fontSize: '1rem', minWidth: '20px', textAlign: 'center' }}>
                                        {item.icon}
                                    </span>
                                    {sidebarOpen && item.label}
                                </NavLink>
                            ))}
                        </>
                    )}
                </nav>

                {/* User area */}
                <div style={{
                    padding: '1rem',
                    borderTop: '1px solid #243b53'
                }}>
                    {sidebarOpen ? (
                        <div>
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.75rem',
                                marginBottom: '0.75rem'
                            }}>
                                <div style={{
                                    width: '32px',
                                    height: '32px',
                                    borderRadius: '50%',
                                    background: '#334e68',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: '#9fb3c8',
                                    fontSize: '0.75rem',
                                    fontWeight: '600',
                                    flexShrink: 0
                                }}>
                                    {user?.firstName?.[0]}{user?.lastName?.[0]}
                                </div>
                                <div style={{ overflow: 'hidden' }}>
                                    <div style={{
                                        color: 'white',
                                        fontSize: '0.8125rem',
                                        fontWeight: '500',
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis'
                                    }}>
                                        {user?.firstName} {user?.lastName}
                                    </div>
                                    <div style={{
                                        color: '#627d98',
                                        fontSize: '0.6875rem',
                                        whiteSpace: 'nowrap'
                                    }}>
                                        {user?.isAdmin ? 'Administrator' : user?.role}
                                    </div>
                                </div>
                            </div>
                            <button
                                onClick={handleLogout}
                                style={{
                                    width: '100%',
                                    background: 'none',
                                    border: '1px solid #334e68',
                                    color: '#829ab1',
                                    padding: '0.375rem',
                                    borderRadius: '6px',
                                    cursor: 'pointer',
                                    fontSize: '0.75rem',
                                    transition: 'all 0.15s'
                                }}
                            >
                                Sign out
                            </button>
                        </div>
                    ) : (
                        <button
                            onClick={handleLogout}
                            style={{
                                background: 'none',
                                border: 'none',
                                color: '#829ab1',
                                cursor: 'pointer',
                                fontSize: '1rem',
                                padding: '0.25rem',
                                display: 'block',
                                margin: '0 auto'
                            }}
                            title="Sign out"
                        >
                            ⏻
                        </button>
                    )}
                </div>
            </div>

            {/* Main content */}
            <div style={{
                marginLeft: sidebarOpen ? '240px' : '64px',
                flex: 1,
                transition: 'margin-left 0.2s',
                minHeight: '100vh',
                display: 'flex',
                flexDirection: 'column'
            }}>
                {/* Top bar */}
                <div style={{
                    background: 'white',
                    borderBottom: '1px solid #d9e2ec',
                    padding: '0 1.5rem',
                    height: '64px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '1.5rem',
                    position: 'sticky',
                    top: 0,
                    zIndex: 50
                }}>
                    <div style={{ color: '#334e68', fontSize: '0.875rem', flexShrink: 0 }}>
                        {new Date().toLocaleDateString('en-GB', {
                            weekday: 'long',
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                        })}
                    </div>
                    <div style={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
                        <TopBarSearch />
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexShrink: 0 }}>
                        <span style={{
                            background: '#f0f4f8',
                            color: '#486581',
                            padding: '0.25rem 0.75rem',
                            borderRadius: '9999px',
                            fontSize: '0.75rem',
                            fontWeight: '500'
                        }}>
                            ATOL 12118
                        </span>
                    </div>
                </div>

                {/* Page content */}
                <div style={{ flex: 1, padding: '1.5rem' }}>
                    <Outlet />
                </div>
            </div>
        </div>
    );
}
